import { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import { useOnClickOutside } from 'utils/onClickOutside'
import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { Menu } from 'components/Menu/Menu'
import { Typography } from 'components/Typography/Typography'
import { ROUTES } from 'utils/route-constants'
import { IconInfo } from 'components/Icons/InfoIcon'
import { useIsMobile } from 'utils/useIsMobile'
import { Button } from 'components/Button/Button'
import { IconPlus } from 'components/Icons/IconPlus'
import { PairInfo } from 'utils/pair-info'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'

const TokenDetails = styled.div`
  // margin: 20px 0;
  display: flex;
  align-items: center;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding-top: 0;
    padding-bottom: 20px;
    flex-direction: column;
    justify-content: flex-start;
  }
`

const StyledMenuItem = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  text-decoration: none;
  justify-content: space-between;
  padding: 16px;
  align-items: center;
  ${p => p.$isActive && `background-color: ${p.theme.colors.background.hover};`}
  &:hover {
    background-color: ${p => p.theme.colors.background.hover};
  }
`

const WhatIsSingleAsset = styled(Typography)`
  cursor: pointer;
  & > div {
    display: flex;
    align-items: center;
    cursor: pointer;
  }
  & > div > svg {
    margin-right: 8px;
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
  }

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    display: flex;
    align-items: center;
  }
`

const NewPositionButton = styled(Button)`
  display: flex;
  align-items: center;
  margin-left: 16px;

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    min-width: 176px;
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 8px;
  }
  & > svg {
    margin-left: 8px;
  }
`

interface Props {
  isLpVault?: boolean
  displayInfo: PairInfo<string>
  isDontShowSelected: boolean
  onResetDontShowThisAgain: () => void
  displayInfoList: PairInfo<string>[]
  handleNewPositionClick: () => void
  newPositionDisabled: boolean
}

interface MenuItemProps {
  item: PairInfo<string>
  toggleMenu: () => void
  isActive: boolean
}

const MenuItem = ({ item, toggleMenu, isActive }: MenuItemProps) => {
  // const vaultInfo = useVaultInfo(item.vault, { refetchInterval: 5000 })
  // const estApy = vaultInfo.apy !== undefined ? round(vaultInfo.apy * 100, 2) + '%' : ''
  return (
    <StyledMenuItem
      key={`${item.coinX.displaySymbol}-${item.coinY.displaySymbol}`}
      to={`/${ROUTES.LP_VAULTS}/${item.coinX.displaySymbol}-${item.coinY.displaySymbol}`}
      onClick={toggleMenu}
      $isActive={isActive}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={item.coinX.iconUrl}
          width={32}
          height={32}
          alt={item.coinX.displaySymbol}
          style={{ borderRadius: '50%' }}
        />
        <img
          src={item.coinY.iconUrl}
          width={32}
          height={32}
          alt={item.coinY.displaySymbol}
          style={{ borderRadius: '50%' }}
        />
        <Typography variant="label2" fontWeight="medium" style={{ marginLeft: 8 }}>
          {`${item.coinX.displaySymbol}-${item.coinY.displaySymbol}`}
        </Typography>
      </div>
      <Typography variant="label2" fontWeight="medium">
        {item.initialAprValue}
      </Typography>
    </StyledMenuItem>
  )
}

export const LpVaultsMenu = ({
  handleNewPositionClick,
  displayInfo,
  isDontShowSelected,
  onResetDontShowThisAgain,
  displayInfoList,
  newPositionDisabled,
}: Props) => {
  const walletAddress = useCurrentWalletAddress()
  const isConnected = !!walletAddress

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const isMobile = useIsMobile()

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <TokenDetails>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={displayInfo.coinX.iconUrl}
            width={isMobile ? 24 : 40}
            height={isMobile ? 24 : 40}
            alt={displayInfo.coinX.displaySymbol}
            style={{ borderRadius: '50%' }}
          />
          <img
            src={displayInfo.coinY.iconUrl}
            width={isMobile ? 24 : 40}
            height={isMobile ? 24 : 40}
            alt={displayInfo.coinY.displaySymbol}
            style={{ borderRadius: '50%' }}
          />
          <div
            style={{ marginLeft: 16, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={toggleMenu}
          >
            <Typography
              variant={isMobile ? 'description' : 'subtitle1'}
              fontWeight="bold"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span
                style={{ marginRight: 16 }}
              >{`${displayInfo.coinX.displaySymbol}-${displayInfo.coinY.displaySymbol}`}</span>
              <IconArrowDown width={isMobile ? 16 : 32} height={isMobile ? 16 : 32} />
            </Typography>
          </div>
        </div>
        {isDontShowSelected && (
          <div style={isMobile ? { marginTop: 8 } : { marginLeft: 'auto' }}>
            <WhatIsSingleAsset variant="label1" fontWeight="light">
              <div onClick={onResetDontShowThisAgain}>
                <IconInfo />
                What are LP Vaults
              </div>
              <NewPositionButton onClick={handleNewPositionClick} disabled={!isConnected || newPositionDisabled}>
                New Position <IconPlus />
              </NewPositionButton>
            </WhatIsSingleAsset>
          </div>
        )}
      </TokenDetails>
      {isMenuOpen && (
        <Menu anchorElement={menuNode} className="mobile" style={{ top: 80, left: 0, padding: '8px 0', zIndex: 3 }}>
          {displayInfoList.map(d => (
            <MenuItem
              key={`${d.coinX.displaySymbol}-${d.coinY.displaySymbol}`}
              item={d}
              toggleMenu={toggleMenu}
              isActive={
                `${d.coinX.displaySymbol.toUpperCase()}-${d.coinY.displaySymbol.toUpperCase()}` ===
                `${displayInfo.coinX.displaySymbol.toUpperCase()}-${displayInfo.coinY.displaySymbol.toUpperCase()}`
              }
            />
          ))}
        </Menu>
      )}
    </div>
  )
}
